import { HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { getState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { combineLatestWith, EMPTY, exhaustMap, pipe } from 'rxjs';
import { ApiErrorResponse } from '@bn/api-interfaces';
import { AuthService } from '@bn/auth/services';
import { UserStateService } from '@bn/auth/stores';
import {
  Application,
  DeleteAllVolumeInstancesRequestParams,
  DeleteVolumeInstanceRequestParams,
  DetachVolumeRequestParams,
  ForbiddenPortsService,
  LimitsService,
  ListApplicationsResponse,
  ListVolumesResponse,
  UpdateVolumeRequestParams,
  VolumesService,
} from '@bn/mc-api/v3';
import { getStoreEvents, StoreRequest, withDefaultRequestStatusState, withSingleEntity, withTranslation } from '@bn/shared/state-store';
import { BnToastrService } from '@bn/web-ui/toastr';
import { getMagicContainerUserProductLimits, MagicContainerUserProductLimitsType } from './helpers/magic-container-store.helper';

// Define events for store actions
const events = getStoreEvents('MC', ['FORBIDDEN_PORTS', 'LIMITS', 'OPTIMAL_BASE_REGION', 'VOLUMES', 'VOLUMES_UPDATE', 'TOTAL_APPLICATIONS'], ['UPDATE']);

type MagicContainerStoreModel = {
  forbiddenPorts: number[];
  userProductLimits: MagicContainerUserProductLimitsType;
  optimalBaseRegion: string;
  volumes: ListVolumesResponse | null;
};

const initialState: MagicContainerStoreModel = {
  forbiddenPorts: [],
  // TODO: limits are fetched from limits endpoint + trial endpoint gives some limits, but we don't have trial data (null) just yet
  // Lower value from each endpoint will win probably, which is handled via helper method that will do last decision
  userProductLimits: getMagicContainerUserProductLimits(null, null),
  optimalBaseRegion: '',
  volumes: null,
};

export const MagicContainerStore = signalStore(
  { providedIn: 'root' },
  withDevtools('MagicContainers'),
  withState<MagicContainerStoreModel>(initialState),
  withDefaultRequestStatusState([StoreRequest.Item]),
  withSingleEntity<ListApplicationsResponse>(),
  withTranslation(),
  withMethods((store) => ({
    // Computed results from any source
    canAddApplication: computed(() => {
      const { current, max } = (getState(store)?.userProductLimits ?? getMagicContainerUserProductLimits(null, null)).applications;
      return max === -1 || max > current;
    }),
  })),
  // User product limits
  withMethods(
    (
      store,
      destroyRef = inject(DestroyRef),
      userStateService = inject(UserStateService),
      forbiddenPortsService = inject(ForbiddenPortsService),
      limitsService = inject(LimitsService),
      volumeService = inject(VolumesService),
      toastr = inject(BnToastrService),
    ) => ({
      getUserProductLimits: rxMethod<void>(
        pipe(
          exhaustMap(() => {
            updateState(store, events.LIMITS.REQUEST, store.setLoading(StoreRequest.List, ['user-product-limits']));
            return limitsService.getLimits();
          }),
          combineLatestWith(userStateService.userTrial$),
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: ([userProductLimits, userTrial]) =>
              updateState(
                store,
                events.LIMITS.SUCCESS,
                { userProductLimits: getMagicContainerUserProductLimits(userProductLimits, userTrial) },
                store.setFinished(StoreRequest.List, ['user-product-limits']),
              ),
            error: ({ error }: HttpErrorResponse) => updateState(store, events.LIMITS.ERROR, store.setError(StoreRequest.List, error, ['user-product-limits'])),
          }),
        ),
      ),
      getForbiddenPorts: rxMethod<void>(
        exhaustMap(() => {
          updateState(store, events.FORBIDDEN_PORTS.REQUEST, store.setLoading(StoreRequest.List, ['mc-forbidden-ports']));
          return forbiddenPortsService.getForbiddenPorts().pipe(
            tapResponse({
              next: (forbiddenPorts) => {
                updateState(store, events.FORBIDDEN_PORTS.SUCCESS, { forbiddenPorts }, store.setFinished(StoreRequest.List, ['mc-forbidden-ports']));
              },
              error: ({ error }: HttpErrorResponse) =>
                updateState(store, events.FORBIDDEN_PORTS.ERROR, store.setError(StoreRequest.List, error, ['mc-forbidden-ports'])),
            }),
          );
        }),
      ),
      setOptimalBaseRegion: (region: string) => {
        updateState(store, events.OPTIMAL_BASE_REGION.UPDATE, { optimalBaseRegion: region });
      },
      getPersistentVolumes: rxMethod<{ appId: Application['id'] }>(
        exhaustMap(({ appId }) => {
          if (!appId) throw Error('Application id is not provided');

          updateState(store, events.VOLUMES.REQUEST, store.setLoading(StoreRequest.List, ['mc-volumes']));

          return volumeService.listVolumes({ appId }).pipe(
            tapResponse({
              next: (data) => {
                updateState(store, events.VOLUMES.SUCCESS, { volumes: data }, store.setFinished(StoreRequest.List, ['mc-volumes']));
              },
              error: (err: ApiErrorResponse) => updateState(store, events.VOLUMES.ERROR, store.setError(StoreRequest.List, err, ['mc-volumes'])),
            }),
          );
        }),
      ),
      clearPersistentVolumes: () => updateState(store, events.VOLUMES.CLEAR, { volumes: null }, store.setDefault([StoreRequest.List])),

      updateVolume: rxMethod<UpdateVolumeRequestParams>(
        exhaustMap(({ appId, volumeId, patchVolumeRequest }) => {
          const translateService = store.translateService();

          if (!volumeId || !appId) {
            toastr.danger(translateService.instant('ERROR.METHOD-PARAMS.IDS', { ids: "'volumeId', 'appId'" }));

            return EMPTY;
          }

          const volumes = getState(store)?.volumes?.items ?? [];
          const volumeIndex = volumes.findIndex((item) => item.id === volumeId);
          const volume = volumes[volumeIndex];

          updateState(store, events.VOLUMES_UPDATE.REQUEST, store.setLoading(StoreRequest.List, ['mc-volumes']));

          return volumeService
            .updateVolume({
              appId,
              volumeId,
              patchVolumeRequest,
            })
            .pipe(
              tapResponse({
                next: () => {
                  if (!volumeId || volumeIndex === -1) return;
                  // Update reference
                  Object.assign(volume, patchVolumeRequest);
                  updateState(store, events.VOLUMES_UPDATE.SUCCESS, { volumes: { items: volumes } }, store.setFinished(StoreRequest.List, ['mc-volumes']));
                  toastr.success('Successfully updated volume.');
                },
                error: ({ error }) => {
                  updateState(store, events.VOLUMES_UPDATE.ERROR, store.setError(StoreRequest.List, error, ['mc-volumes']));
                  toastr.danger(`Error while trying to update volume.`);
                },
              }),
            );
        }),
      ),

      detachVolume: rxMethod<DetachVolumeRequestParams>(
        exhaustMap(({ appId, volumeId }) => {
          const translateService = store.translateService();

          if (!volumeId || !appId) {
            toastr.danger(translateService.instant('ERROR.METHOD-PARAMS.IDS', { ids: "'volumeId', 'appId'" }));

            return EMPTY;
          }

          const volumes = getState(store)?.volumes?.items ?? [];
          const volume = volumes.find((item) => item.id === volumeId);

          return volumeService
            .detachVolume({
              appId,
              volumeId,
            })
            .pipe(
              tapResponse({
                next: () => {
                  toastr.success(`Successfully started detaching all instances from volume ${volume?.name}`);
                },
                error: () => {
                  toastr.danger(`Error while trying to detach all instances from volume ${volume?.name}`);
                },
              }),
            );
        }),
      ),

      deleteVolumeInstance: rxMethod<DeleteVolumeInstanceRequestParams>(
        exhaustMap(({ appId, volumeId, instanceId }) => {
          const translateService = store.translateService();

          if (!volumeId || !instanceId || !appId) {
            toastr.danger(translateService.instant('ERROR.METHOD-PARAMS.IDS', { ids: "'volumeId', 'instanceId', 'appId'" }));

            return EMPTY;
          }

          const volumes = getState(store)?.volumes?.items ?? [];
          const volume = volumes.find((item) => item.id === volumeId);

          return volumeService
            .deleteVolumeInstance({
              appId,
              volumeId,
              instanceId,
            })
            .pipe(
              tapResponse({
                next: () => {
                  toastr.success(`Instance ${instanceId} on volume "${volume?.name}" is being deleted successfully`);
                },
                error: () => {
                  toastr.success(`Error while trying to delete instance ${instanceId} on volume "${volume?.name}`);
                },
              }),
            );
        }),
      ),

      deleteAllVolumeInstances: rxMethod<DeleteAllVolumeInstancesRequestParams>(
        exhaustMap(({ appId, volumeId }) => {
          const translateService = store.translateService();

          if (!volumeId || !appId) {
            toastr.danger(translateService.instant('ERROR.METHOD-PARAMS.IDS', { ids: "'volumeId', 'appId'" }));

            return EMPTY;
          }

          const volumes = getState(store)?.volumes?.items ?? [];
          const volume = volumes.find((item) => item.id === volumeId);

          return volumeService
            .deleteAllVolumeInstances({
              appId,
              volumeId,
            })
            .pipe(
              tapResponse({
                next: () => {
                  toastr.success(`Successfully started deleting all instances from volume ${volume?.name}`);
                },
                error: () => {
                  toastr.danger(`Error while trying to delete all instances from volume ${volume?.name}`);
                },
              }),
            );
        }),
      ),
    }),
  ),
  withHooks({
    onInit({ getForbiddenPorts, getUserProductLimits }) {
      const authService = inject(AuthService);

      if (authService.isUserLoggedIn()) {
        getUserProductLimits();
        getForbiddenPorts();
      }
    },
  }),
);
